// =====================================================================
// Employee feature — statutory tables, role/designation defaults, and
// pure helper functions (PT/LWF/TDS/formatting).
//
// Extracted from pages/Employees.tsx without any logic changes.
// =====================================================================

// ==================== STATUTORY DATA TABLES ====================

// Professional Tax — state-wise monthly slabs (FY 2025-26)
export const PT_STATES: Record<
  string,
  { label: string; slabs: number[][]; annual: boolean; note?: string }
> = {
  karnataka: { label: "Karnataka", slabs: [[0, 14999, 0], [15000, 99999999, 200]], annual: false },
  maharashtra: { label: "Maharashtra", slabs: [[0, 7500, 0], [7501, 10000, 175], [10001, 99999999, 200]], annual: false },
  westbengal: { label: "West Bengal", slabs: [[0, 8500, 0], [8501, 10000, 90], [10001, 15000, 110], [15001, 25000, 130], [25001, 40000, 150], [40001, 99999999, 200]], annual: false },
  telangana: { label: "Telangana", slabs: [[0, 14999, 0], [15000, 20000, 150], [20001, 99999999, 200]], annual: false },
  tamilnadu: { label: "Tamil Nadu", slabs: [[0, 3500, 0], [3501, 5000, 22], [5001, 7500, 33], [7501, 10000, 52], [10001, 12500, 63], [12501, 15000, 83], [15001, 20000, 104], [20001, 25000, 135], [25001, 99999999, 182]], annual: false },
  gujarat: { label: "Gujarat", slabs: [[0, 5999, 0], [6000, 8999, 80], [9000, 11999, 150], [12000, 99999999, 200]], annual: false },
  kerala: { label: "Kerala", slabs: [[0, 99999999, 208]], annual: false },
  andhra: { label: "Andhra Pradesh", slabs: [[0, 14999, 0], [15000, 20000, 150], [20001, 99999999, 200]], annual: false },
  mp: { label: "Madhya Pradesh", slabs: [[0, 18749, 0], [18750, 25000, 125], [25001, 33333, 167], [33334, 99999999, 208]], annual: false },
  assam: { label: "Assam", slabs: [[0, 9999, 0], [10000, 14999, 150], [15000, 24999, 180], [25000, 99999999, 208]], annual: false },
  bihar: { label: "Bihar", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Bihar" },
  delhi: { label: "Delhi", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Delhi" },
  rajasthan: { label: "Rajasthan", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Rajasthan" },
  none: { label: "Not Applicable", slabs: [[0, 99999999, 0]], annual: false },
};

// Labour Welfare Fund — state-wise
export const LWF_STATES: Record<
  string,
  { label: string; ee: number; er: number; freq: string }
> = {
  andhra: { label: "Andhra Pradesh", ee: 20, er: 40, freq: "annual" },
  delhi: { label: "Delhi", ee: 0.75, er: 2.25, freq: "monthly" },
  gujarat: { label: "Gujarat", ee: 6, er: 12, freq: "halfyearly" },
  haryana: { label: "Haryana", ee: 25, er: 50, freq: "monthly" },
  karnataka: { label: "Karnataka", ee: 20, er: 40, freq: "annual" },
  kerala: { label: "Kerala", ee: 4, er: 8, freq: "halfyearly" },
  madhyapradesh: { label: "Madhya Pradesh", ee: 10, er: 20, freq: "halfyearly" },
  maharashtra: { label: "Maharashtra", ee: 25, er: 75, freq: "halfyearly" },
  odisha: { label: "Odisha", ee: 10, er: 20, freq: "annual" },
  punjab: { label: "Punjab", ee: 10, er: 20, freq: "monthly" },
  rajasthan: { label: "Rajasthan", ee: 25, er: 50, freq: "monthly" },
  tamilnadu: { label: "Tamil Nadu", ee: 10, er: 20, freq: "halfyearly" },
  telangana: { label: "Telangana", ee: 20, er: 40, freq: "annual" },
  westbengal: { label: "West Bengal", ee: 3, er: 15, freq: "monthly" },
  none: { label: "Not Applicable", ee: 0, er: 0, freq: "monthly" },
};

// EPF wage ceiling. Raised ₹15,000 → ₹25,000 by MoLE notification S.O. 5109(E)
// dated 17 Sep 2026 — the first revision since 2014. ONE ceiling governs all
// three statutory shares: the 12% PF base, the employer's 8.33% EPS share and
// the 0.5% EDLI share. So at the ceiling employee PF goes ₹1,800 → ₹3,000,
// EPS ₹1,250 → ₹2,083 and EDLI ₹75 → ₹125. It also widens the band in which
// EPF is MANDATORY, which is why an employee on ₹18,000 Basic can no longer
// opt out.
export const PF_CEILING_REVISED_FROM = "2026-09-17";

// The ceiling in force on `effectiveDate` — a salary component's own start date.
// Resolved PER COMPONENT, never globally: a component that began before the
// revision must keep pricing at ₹15,000, so re-saving an old slice (e.g. an
// April 2026 component reopened for an unrelated edit) cannot silently
// re-price history at the new ceiling. No date = the ceiling in force today,
// which is what a brand-new component should use.
export function pfWageCeiling(effectiveDate?: string | Date | null): number {
  const parsed = effectiveDate ? new Date(effectiveDate) : new Date();
  const on = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  return on >= new Date(PF_CEILING_REVISED_FROM) ? 25000 : 15000;
}

// Employer's EPF contribution — fixed by statute, never company-configurable.
export const PF_EMPLOYER_RATE_PCT = 12;

// ESIC — monthly gross ceiling for coverage, and the two contribution rates.
// Below/at the ceiling ESIC applies; above it, it does not.
export const ESIC_WAGE_CEILING = 21000;
export const ESIC_EMPLOYEE_RATE_PCT = 0.75;
export const ESIC_EMPLOYER_RATE_PCT = 3.25;

// TAX COMPUTATION FY 2025-26 (AY 2026-27)

// Standard deduction, per regime.
export const STD_DEDUCTION_NEW = 75000;
export const STD_DEDUCTION_OLD = 50000;
// Section 87A rebate and the taxable-income limit it applies up to, per regime.
export const REBATE_87A_NEW = 60000;
export const REBATE_87A_LIMIT_NEW = 1200000;
export const REBATE_87A_OLD = 12500;
export const REBATE_87A_LIMIT_OLD = 500000;

// New Tax Regime slabs (Budget 2025)
export function taxNew(taxable: number): number {
  const slabs: number[][] = [
    [0, 400000, 0],
    [400001, 800000, 0.05],
    [800001, 1200000, 0.10],
    [1200001, 1600000, 0.15],
    [1600001, 2000000, 0.20],
    [2000001, 2400000, 0.25],
    [2400001, Infinity, 0.30],
  ];
  let tax = 0;
  for (const [lo, hi, rate] of slabs) {
    if (taxable <= lo) break;
    tax += (Math.min(taxable, hi) - lo) * rate;
  }
  // Rebate u/s 87A: zero tax (rebate up to REBATE_87A_NEW) if taxable is at or
  // under REBATE_87A_LIMIT_NEW.
  if (taxable <= REBATE_87A_LIMIT_NEW) {
    tax = Math.max(0, tax - Math.min(tax, REBATE_87A_NEW));
  } else {
    // Marginal relief: just above the rebate limit the tax payable shall not
    // exceed the income that exceeds it, so a small raise isn't wiped out by
    // tax. For higher incomes the slab tax is lower than the excess, so
    // Math.min leaves it unchanged.
    tax = Math.min(tax, taxable - REBATE_87A_LIMIT_NEW);
  }
  return tax + tax * 0.04; // + 4% cess
}

// Old Tax Regime slabs (below 60 years)
export function taxOld(taxable: number): number {
  const slabs: number[][] = [
    [0, 250000, 0],
    [250001, 500000, 0.05],
    [500001, 1000000, 0.20],
    [1000001, Infinity, 0.30],
  ];
  let tax = 0;
  for (const [lo, hi, rate] of slabs) {
    if (taxable <= lo) break;
    tax += (Math.min(taxable, hi) - lo) * rate;
  }
  // Rebate u/s 87A: up to REBATE_87A_OLD if taxable is at or under the limit.
  if (taxable <= REBATE_87A_LIMIT_OLD) tax = Math.max(0, tax - Math.min(tax, REBATE_87A_OLD));
  return tax + tax * 0.04; // + 4% cess
}

export interface TaxDeclaration {
  sec80c?: number;
  sec80d?: number;
  sec80ccd1b?: number;
  hraExempt?: number;
  homeLoan?: number;
  ltaExempt?: number;
  sec80g?: number;
  npsEmployer?: number;
  ptPaid?: number;
  // Surfaced on the self-service Investment Declaration tab.
  sec80tta?: number; // Interest from savings deposits — max ₹10,000
  sec80eea?: number; // Interest on housing loan (first-time) — max ₹1,50,000
  sec80e?: number;   // Interest on education loan (80E) — no monetary cap
  sec80u?: number;   // Disability (80U / 80DD) — ₹75,000 normal, ₹1,25,000 severe
  otherDed?: number; // Any other deduction
}

// Monthly ceiling on the meal-allowance component in the CTC auto-split: if the
// even 1/7 share of the allowance pool would exceed this, meal is fixed here and
// the surplus is spread across the other allowances.
export const MEAL_ALLOWANCE_MONTHLY_CAP = 8800;
// Annual voucher exemption ceiling = the monthly meal cap × 12. A VOUCHER meal
// allowance is exempt up to this in the tax calc; CASH is never exempt.
export const MEAL_VOUCHER_ANNUAL_CAP = MEAL_ALLOWANCE_MONTHLY_CAP * 12; // ₹1,05,600

// Annual taxable income after all deductions. Per company policy here, the
// OLD regime subtracts BOTH the employee's and the employer's PF (in addition
// to the standard deduction and the declared chapter-VI-A deductions). The NEW
// regime subtracts ONLY the employer's PF — the employee's own PF contribution
// is not deductible there. PF is subtracted directly, so it is NOT also folded
// into the 80C cap. `mealVoucherExemptAnnual` is the FY meal-voucher amount to
// exempt (already summed across components, and only non-zero when the meal
// allowance is paid as VOUCHER); it's capped at MEAL_VOUCHER_ANNUAL_CAP here and
// applied in BOTH regimes — taken off gross before the standard deduction.
export function calcTaxableIncome(
  grossAnnual: number,
  regime: string,
  declaration: TaxDeclaration,
  employeePFAnnual: number,
  employerPFAnnual: number = 0,
  mealVoucherExemptAnnual: number = 0,
): number {
  const pfAnnual = (employeePFAnnual || 0) + (employerPFAnnual || 0);

  if (regime === "new") {
    // Meal vouchers (food coupons) are exempt up to MEAL_VOUCHER_ANNUAL_CAP
    // and are taken off gross salary BEFORE the standard deduction. Company
    // policy here allows this exemption in the NEW regime too; everything else
    // stays stripped. When the meal allowance is paid as CASH the caller passes
    // 0, so nothing is exempted.
    const mealVoucherAnnual = Math.min(Math.max(0, mealVoucherExemptAnnual || 0), MEAL_VOUCHER_ANNUAL_CAP);
    const afterMeal = Math.max(0, grossAnnual - mealVoucherAnnual);
    // The standard deduction can't exceed the salary it's applied to: when the
    // (post-meal) gross is below the statutory cap, the whole of it becomes the
    // deduction (so taxable income is 0, never negative).
    const stdDeduction = Math.min(STD_DEDUCTION_NEW, afterMeal);
    const npsEmployerAnnual = (declaration.npsEmployer || 0) * 12;
    // New regime: only the EMPLOYER's PF reduces taxable income (the employee's
    // own PF is not deductible here).
    return Math.max(0, afterMeal - stdDeduction - (employerPFAnnual || 0) - npsEmployerAnnual);
  }

  // Meal-voucher exemption, capped at MEAL_VOUCHER_ANNUAL_CAP.
  const mealVoucherAnnual = Math.min(Math.max(0, mealVoucherExemptAnnual || 0), MEAL_VOUCHER_ANNUAL_CAP);

  // Old regime deductions. Standard deduction capped at the gross (see the
  // new-regime note above).
  const stdDeduction = Math.min(STD_DEDUCTION_OLD, Math.max(0, grossAnnual));
  const hraExemptAnnual = declaration.hraExempt || 0;
  const ltaExemptAnnual = declaration.ltaExempt || 0;
  const sec80cAnnual = Math.min(declaration.sec80c || 0, 150000);
  // 80D: self+family ₹25,000 + parents ₹25,000 (<60) or ₹50,000 (≥60) → ₹75,000 max.
  const sec80dAnnual = Math.min(declaration.sec80d || 0, 75000);
  const sec80ccd1bAnnual = Math.min(declaration.sec80ccd1b || 0, 50000);
  const homeLoanAnnual = Math.min(declaration.homeLoan || 0, 200000);
  const sec80gAnnual = declaration.sec80g || 0;
  const ptAnnual = (declaration.ptPaid || 0) * 12;
  const sec80ttaAnnual = Math.min(declaration.sec80tta || 0, 10000);
  const sec80eeaAnnual = Math.min(declaration.sec80eea || 0, 150000);
  // 80E: interest on education loan — fully deductible, no monetary cap.
  const sec80eAnnual = declaration.sec80e || 0;
  // 80U / 80DD: disability — ₹75,000 (normal) up to ₹1,25,000 (severe).
  const sec80uAnnual = Math.min(declaration.sec80u || 0, 125000);
  const otherDedAnnual = declaration.otherDed || 0;

  return Math.max(
    0,
    grossAnnual - stdDeduction - pfAnnual - hraExemptAnnual - ltaExemptAnnual -
    sec80cAnnual - sec80dAnnual - sec80ccd1bAnnual - homeLoanAnnual - sec80gAnnual -
    ptAnnual - sec80ttaAnnual - sec80eeaAnnual - sec80eAnnual - sec80uAnnual - otherDedAnnual -
    mealVoucherAnnual
  );
}

// Monthly income-tax (TDS) — slab tax + 87A rebate + marginal relief +
// 4% cess, divided by 12. Excludes PT/LWF (those are added separately by
// the caller). employerPFAnnual is optional for backward compatibility.
export function calcTDS(
  grossAnnual: number,
  regime: string,
  declaration: TaxDeclaration,
  employeePFAnnual: number,
  employerPFAnnual: number = 0,
  mealVoucherExemptAnnual: number = 0,
): number {
  const taxableIncome = calcTaxableIncome(
    grossAnnual,
    regime,
    declaration,
    employeePFAnnual,
    employerPFAnnual,
    mealVoucherExemptAnnual,
  );
  const annualTax = regime === "new" ? taxNew(taxableIncome) : taxOld(taxableIncome);
  return Math.round(annualTax / 12);
}

// ==================== FINANCIAL-YEAR PRORATED TAX ====================
//
// The single-record calcTDS above annualises ONE monthly gross as if it
// applied for the whole year. That is correct for a flat salary, but when a
// salary is revised mid-year (or the employee worked elsewhere earlier in the
// same FY) the projected annual income is a BLEND of every component's CTC for
// the months it was actually in force. calcFyTax below builds that blend, taxes
// it once for the year, credits any TDS already deducted by a previous
// employer, and spreads the balance over the months worked. Nothing here
// changes calcTDS / taxNew / taxOld — they are reused as-is.

// Indian financial year runs 1 Apr → 31 Mar. Label format: "2025-26".
export function currentFinancialYear(today: Date = new Date()): string {
  const y = today.getFullYear();
  // Jan–Mar belong to the FY that started the previous calendar year.
  const startYear = today.getMonth() >= 3 ? y : y - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
}

// First/last calendar day of a "2025-26" financial year.
export function fyBounds(fy: string): { start: Date; end: Date } {
  const startYear = parseInt(fy.split("-")[0], 10);
  return {
    start: new Date(startYear, 3, 1), // 1 Apr
    end: new Date(startYear + 1, 2, 31), // 31 Mar
  };
}

// A list of recent financial years (newest first) for the form dropdown.
// Selectable financial years: the CURRENT (running) FY and previous ones only
// — never a future year. Defaults to just the running year + the previous one.
export function financialYearOptions(count: number = 2, today: Date = new Date()): string[] {
  const current = currentFinancialYear(today);
  const startYear = parseInt(current.split("-")[0], 10);
  return Array.from({ length: count }, (_, i) => {
    const sy = startYear - i; // current first, then go back — no future years
    return `${sy}-${String((sy + 1) % 100).padStart(2, "0")}`;
  });
}

// Whole months a [effStart, effEnd] window overlaps the financial year. A
// component owns a month if it is in force on the LAST day of that month, so a
// component that takes effect mid-month still claims the whole month (and one
// that ends mid-month does not). This attributes a boundary month to the newer
// component — matching how the month-wise table picks each month's active CTC,
// and so a salary effective e.g. 5 Jan counts January, not from February. The
// result is clamped to [0, 12].
export function monthsActiveInFy(
  effStart: Date | null,
  effEnd: Date | null,
  fyStart: Date,
  fyEnd: Date,
): number {
  const start = effStart && effStart > fyStart ? effStart : fyStart;
  const end = effEnd && effEnd < fyEnd ? effEnd : fyEnd;
  if (end < start) return 0;
  let months = 0;
  // Walk the 12 months of the FY and count those in force on the month's last day.
  for (let m = 0; m < 12; m++) {
    const monthEnd = new Date(fyStart.getFullYear(), fyStart.getMonth() + m + 1, 0);
    if (monthEnd >= start && monthEnd <= end) months++;
  }
  return Math.min(12, Math.max(0, months));
}

export interface FySlice {
  monthlyGross: number; // taxable gross per month for this component
  months: number; // months this component is in force within the FY
  pfEEMonthly: number; // employee PF per month (reduces taxable income)
  pfERMonthly: number; // employer PF per month (reduces taxable income here)
  mealVoucherMonthly?: number; // meal allowance paid as VOUCHER per month (exempt up to the annual cap, both regimes)
}

export interface PriorEmployment {
  taxableIncome: number; // annual taxable income already earned this FY elsewhere
  tdsPaid: number; // TDS already deducted by the previous employer
}

export interface FyTaxResult {
  annualGross: number; // blended projected annual income (incl. prior employer)
  annualTaxable: number; // after standard deduction, PF and declarations
  annualTax: number; // full-year income tax (incl. 4% cess, after rebate)
  priorTds: number; // TDS already paid elsewhere, credited
  balanceTax: number; // tax still to deduct = max(0, annualTax − priorTds)
  monthsWorked: number; // current-employer months in the FY (>= 1)
  monthlyTds: number; // balanceTax spread evenly over monthsWorked
}

// Blend every component across the financial year, tax it once, credit prior
// TDS, and spread the balance over the months worked with this employer.
export function calcFyTax(
  slices: FySlice[],
  regime: string,
  declaration: TaxDeclaration,
  prior: PriorEmployment = { taxableIncome: 0, tdsPaid: 0 },
): FyTaxResult {
  let annualGross = prior.taxableIncome || 0;
  let pfEEAnnual = 0;
  let pfERAnnual = 0;
  let mealVoucherAnnual = 0;
  let monthsWorked = 0;
  for (const s of slices) {
    const months = Math.max(0, s.months || 0);
    annualGross += (s.monthlyGross || 0) * months;
    pfEEAnnual += (s.pfEEMonthly || 0) * months;
    pfERAnnual += (s.pfERMonthly || 0) * months;
    mealVoucherAnnual += (s.mealVoucherMonthly || 0) * months;
    monthsWorked += months;
  }
  const annualTaxable = calcTaxableIncome(annualGross, regime, declaration, pfEEAnnual, pfERAnnual, mealVoucherAnnual);
  const annualTax = regime === "new" ? taxNew(annualTaxable) : taxOld(annualTaxable);
  const priorTds = Math.max(0, prior.tdsPaid || 0);
  const balanceTax = Math.max(0, annualTax - priorTds);
  const effectiveMonths = Math.max(1, monthsWorked);
  return {
    annualGross: Math.round(annualGross),
    annualTaxable: Math.round(annualTaxable),
    annualTax: Math.round(annualTax),
    priorTds: Math.round(priorTds),
    balanceTax: Math.round(balanceTax),
    monthsWorked,
    monthlyTds: Math.round(balanceTax / effectiveMonths),
  };
}

// ==================== SALARY COMPONENT SPLIT ====================

// Standard CTC → monthly component breakup used by both the admin
// add/update employee form and the self-service Salary Declaration tab.
// Basic is fixed at 50% of monthly CTC, HRA at 50% of Basic; the
// remainder is spread as evenly as possible across the six allowances
// (extra ₹1 going to the earliest fields so the parts sum back to CTC).
export const SALARY_BASIC_PCT = 0.5; // Basic = 50% of CTC (floor)
export const SALARY_HRA_PCT = 0.5; // HRA = 50% of Basic (auto-split default)

export interface SalaryComponents {
  basic: number;
  hra: number;
  conveyance: number;
  medicalallowance: number;
  mealAllowance: number;
  allowance: number;
  lta: number;
  others: number;
  bonusAmount: number;
}

export function splitCtcToComponents(monthlyCtc: number): SalaryComponents {
  const basic = Math.round(monthlyCtc * SALARY_BASIC_PCT);
  const hra = Math.round(basic * SALARY_HRA_PCT);

  const remaining = Math.round(monthlyCtc - basic - hra);

  // SIX allowances take an even 1/6 of `remaining`. Bonus/Incentive is NOT a
  // salary component — it's paid per month via Payroll Records — so its former
  // 1/7 share is redistributed across the remaining six, keeping the parts
  // summed to CTC. The MEAL allowance is capped at MEAL_ALLOWANCE_MONTHLY_CAP:
  // when its even share would exceed the cap it's fixed there and the surplus
  // spreads equally across the OTHER five (conveyance, medical, special, lta, others).
  const mealAllowance = Math.min(Math.round(remaining / 6), MEAL_ALLOWANCE_MONTHLY_CAP);

  const otherPool = Math.max(0, remaining - mealAllowance);
  const otherCount = 5;
  const baseShare = Math.floor(otherPool / otherCount);
  const leftover = otherPool - baseShare * otherCount; // 0..4 spare ₹1s
  const otherShares = Array.from({ length: otherCount }, (_, i) =>
    i < leftover ? baseShare + 1 : baseShare
  );
  const [conveyance, medicalallowance, allowance, lta, others] = otherShares;

  // Any float / rounding residual lands in "Other Allowances" so the parts
  // always sum back to the CTC.
  const residual =
    monthlyCtc - (basic + hra + conveyance + medicalallowance + mealAllowance + allowance + lta + others);
  const otherAllowances = Math.round((others + residual) * 100) / 100;

  // bonusAmount kept for type compatibility but always 0 — bonus is a Payroll
  // Records item, never part of the CTC split.
  return { basic, hra, conveyance, medicalallowance, mealAllowance, allowance, lta, others: otherAllowances, bonusAmount: 0 };
}

// Minimum allowed Basic for a given monthly CTC. The Salary Declaration
// tab lets an employee raise Basic but never go below the floor
// (Basic ≥ 50% CTC).
export function salaryFloors(monthlyCtc: number, basic: number) {
  const minBasic = Math.round(monthlyCtc * SALARY_BASIC_PCT);
  const minHra = Math.round((basic || minBasic) * SALARY_HRA_PCT);
  return { minBasic, minHra };
}

// House Rent Allowance tax exemption (old regime). The exempt amount is
// the least of three legal limits:
//   1. Actual HRA received (annual)
//   2. Rent paid (annual) minus 10% of Basic salary (annual)
//   3. 50% of Basic for metro cities (Delhi/Mumbai/Kolkata/Chennai),
//      else 40% of Basic for non-metro
// Returns 0 when the employee doesn't pay rent or receives no HRA.
export function calcHraExemption(
  basicAnnual: number,
  hraReceivedAnnual: number,
  rentPaidAnnual: number,
  isMetro: boolean,
): number {
  if (rentPaidAnnual <= 0 || hraReceivedAnnual <= 0) return 0;
  const cityCap = (isMetro ? 0.5 : 0.4) * basicAnnual;
  const rentMinusTen = Math.max(0, rentPaidAnnual - 0.1 * basicAnnual);
  return Math.max(0, Math.round(Math.min(hraReceivedAnnual, rentMinusTen, cityCap)));
}

// LTA (Leave Travel Allowance) exemption, old regime. The exemption is the
// lowest of the actual travel expense and the LTA received from the employer.
export function calcLtaExemption(
  travelExpenseAnnual: number,
  ltaReceivedAnnual: number,
): number {
  if (travelExpenseAnnual <= 0 || ltaReceivedAnnual <= 0) return 0;
  return Math.max(0, Math.round(Math.min(travelExpenseAnnual, ltaReceivedAnnual)));
}


export const DESIGNATION_OPTIONS = [
  "Backend Developer",
  "Frontend Developer",
  "Full Stack Developer",
  "DevOps Engineer",
  "Data Scientist",
  "QA Engineer",
  "UI/UX Designer",
  "Product Manager",
  "HR Manager",
  "Sales Executive",
  "Marketing Manager",
  "Accountant",
];

// Helper functions
export function calcPT(grossMonthly: number, stateKey: string): number {
  const cfg = PT_STATES[stateKey] || PT_STATES.none;
  for (const [lo, hi, amt] of cfg.slabs) {
    if (grossMonthly >= lo && grossMonthly <= hi) return amt;
  }
  return 0;
}

export function lwfMonthly(stateKey: string): { ee: number; er: number } {
  const s = LWF_STATES[stateKey] || LWF_STATES.none;
  if (s.freq === "monthly") return { ee: s.ee, er: s.er };
  if (s.freq === "halfyearly") return { ee: +(s.ee / 6).toFixed(2), er: +(s.er / 6).toFixed(2) };
  if (s.freq === "annual") return { ee: +(s.ee / 12).toFixed(2), er: +(s.er / 12).toFixed(2) };
  return { ee: 0, er: 0 };
}

export function fmt(n: number | string): string {
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (isNaN(num)) return "0.00";
  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0);
}

export function num(v: any): number {
  return parseFloat(v) || 0;
}

/**
 * Display-only normalization for a person name. The first character of every
 * whitespace-separated word becomes uppercase; all other characters become
 * lowercase. So "JOHN", "john", "JoHn" all render as "John".
 *
 * The stored value is left untouched — this only formats what the UI shows.
 */
export function toProperCase(value?: string | null): string {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Capitalize ONLY the very first character of a string while preserving
 * every other character exactly as the user typed it. Used on name input
 * fields so a stray lowercase first letter is auto-corrected, but later
 * casing the user chose (e.g. "CK", "McDonald", "JoHn") survives untouched.
 *
 *   capitalizeFirstLetter("ck")     === "Ck"
 *   capitalizeFirstLetter("cK")     === "CK"
 *   capitalizeFirstLetter("CK")     === "CK"
 *   capitalizeFirstLetter("JoHn")   === "JoHn"
 *   capitalizeFirstLetter("")       === ""
 */
export function capitalizeFirstLetter(value: string): string {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}
